import React, { useEffect, useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  Alert,
  CircularProgress,
  Typography,
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import { withFirebase } from '../Fb'

// Add, rename and delete for customers/{cid}/eventticketgroups. A group is just a name at
// this point. usageCount tells how many event tickets reference each group, so the delete
// confirmation can say what it is about to orphan - the caller has every ticket in state
// already, so counting is free there.
function EventTicketGroupsDialog({ open, onClose, onChanged, groups, usageCount, firebase }) {
  const [newName, setNewName] = useState('')
  const [editingId, setEditingId] = useState(null)
  const [editingName, setEditingName] = useState('')
  const [confirmDelete, setConfirmDelete] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (open) {
      setNewName('')
      setEditingId(null)
      setEditingName('')
      setConfirmDelete(null)
      setError(null)
    }
  }, [open])

  const run = async (fn) => {
    setBusy(true)
    setError(null)
    try {
      await fn()
      if (onChanged) await onChanged()
    } catch (err) {
      console.error('EventTicketGroupsDialog', err)
      setError(
        err && err.code === 'permission-denied'
          ? 'Ei oikeuksia muokata tapahtumaryhmiä.'
          : (err && err.message) || String(err)
      )
    }
    setBusy(false)
  }

  const handleAdd = () => {
    const name = newName.trim()
    if (!name) return
    run(async () => {
      await firebase.createEventTicketGroup(name)
      setNewName('')
    })
  }

  const handleRename = () => {
    const name = editingName.trim()
    if (!name || !editingId) return
    run(async () => {
      await firebase.updateEventTicketGroup(editingId, name)
      setEditingId(null)
      setEditingName('')
    })
  }

  const handleDelete = () => {
    const group = confirmDelete
    if (!group) return
    run(async () => {
      await firebase.deleteEventTicketGroup(group.id)
      setConfirmDelete(null)
    })
  }

  const list = groups || []

  return (
    <Dialog open={!!open} onClose={busy ? undefined : onClose} fullWidth maxWidth='sm'>
      <DialogTitle>Tapahtumaryhmät</DialogTitle>
      <DialogContent>
        {list.length === 0 && (
          <Typography variant='body2' color='textSecondary' style={{ marginBottom: 12 }}>
            Ei tapahtumaryhmiä. Lisää ensimmäinen alta.
          </Typography>
        )}

        {list.map((g) => {
          const inUse = (usageCount && usageCount[g.id]) || 0
          if (editingId === g.id) {
            return (
              <div key={g.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0' }}>
                <TextField
                  fullWidth
                  value={editingName}
                  onChange={(e) => setEditingName(e.target.value)}
                  disabled={busy}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleRename()
                  }}
                />
                <IconButton onClick={handleRename} disabled={busy || !editingName.trim()} size='small'>
                  <CheckIcon fontSize='small' />
                </IconButton>
                <IconButton onClick={() => setEditingId(null)} disabled={busy} size='small'>
                  <CloseIcon fontSize='small' />
                </IconButton>
              </div>
            )
          }
          return (
            <div
              key={g.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '6px 0',
                borderBottom: '1px solid #eee',
              }}
            >
              <div style={{ flex: 1 }}>
                {g.name}
                <Typography variant='body2' color='textSecondary' component='span' style={{ marginLeft: 8 }}>
                  {inUse === 1 ? '1 tapahtuma' : inUse + ' tapahtumaa'}
                </Typography>
              </div>
              <IconButton
                onClick={() => {
                  setEditingId(g.id)
                  setEditingName(g.name || '')
                }}
                disabled={busy}
                size='small'
              >
                <EditIcon fontSize='small' />
              </IconButton>
              <IconButton onClick={() => setConfirmDelete(g)} disabled={busy} size='small'>
                <DeleteIcon fontSize='small' />
              </IconButton>
            </div>
          )
        })}

        {/* Deleting is a soft delete and deliberately leaves the event tickets alone, so say
            what happens to the ones still pointing at it. */}
        {confirmDelete && (
          <Alert severity='warning' style={{ marginTop: 12 }}>
            Poistetaanko ryhmä <b>{confirmDelete.name}</b>?
            {((usageCount && usageCount[confirmDelete.id]) || 0) > 0 && (
              <>
                {' '}
                Ryhmään kuuluu <b>{usageCount[confirmDelete.id]}</b>{' '}
                {usageCount[confirmDelete.id] === 1 ? 'tapahtuma' : 'tapahtumaa'}. Tapahtumat
                säilyvät, mutta niiden ryhmä näkyy jatkossa muodossa "(poistettu)".
              </>
            )}
            <div style={{ marginTop: 8 }}>
              <Button onClick={handleDelete} color='error' disabled={busy} size='small'>
                Poista ryhmä
              </Button>
              <Button onClick={() => setConfirmDelete(null)} disabled={busy} size='small'>
                Peruuta
              </Button>
            </div>
          </Alert>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 16 }}>
          <TextField
            fullWidth
            label='Uusi ryhmä'
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            disabled={busy}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleAdd()
            }}
          />
          <Button
            onClick={handleAdd}
            disabled={busy || !newName.trim()}
            startIcon={<AddCircleIcon />}
            color='primary'
          >
            Lisää
          </Button>
        </div>

        {error && (
          <Alert severity='error' style={{ marginTop: 12 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Sulje {busy ? <CircularProgress size={12} /> : ''}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default withFirebase(EventTicketGroupsDialog)
